import React, { useEffect, useState } from 'react';
import { createContext } from 'react';
import { useStoreDetails } from '../StoreDetailsProvider/UseStoreDetails';
import { getHeaderData, getTableData, getAvaliableSheet } from '../../../api/Product';
import { calculatePricedEquipmentData, getPriceTableByBrandAndDate } from '../../../reservation/CalcPrice';
import { formatDateString } from '../../Utils';
import { getDeliveryCharge } from '../../../api/Product';

export interface ReservationMainProps {
  pickup: Date | null; // start date
  dropoff: Date | null; // end date
  prices: {
    subtotal: number;
    tax: number;
    discount: number;
    service_fee: number;
    total: number;
  },
  price_table_id: number | null;
  name: string;
  note: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  state_id: number;
  postal_code: string;
  phone_number: string;
  email: string;
  password: string;
  is_accept: boolean;
  img_url: string,
  address_id: number | null,
  selectedAddress: any,
  use_manual: boolean;
  manual_address: string,
  usps_address: string,
  mapbox_id: string | null,
  lat: number | null,
  lng: number | null,
  driver_tip: number,
  discount_code: string,
  promo_code: number | null,
  discount_rate: number | null,
  headerData: Array<any>,
  priceTableData: Array<any>,
  availableSheet: any,
  license_file: File | null;
  additional_license_files: File[] | null;
  insurance_file: File | null;
  additional_insurance_files: File[] | null;
  agreement_file: File | null,
  pickup_time: string | null, // start time
  dropoff_time: string | null, // end time
  discountApplied: boolean,
  bodhi_protect_opted_out: boolean,
  bodhi_protect_price: number,
  
  // Driving Questions
  is_only_driver: boolean | null;
  additional_drivers: { name: string; email: string; phone: string }[] | null;
  confirm_drivers: boolean;
  speed_limit_agreement: boolean;
  damage_responsibility: boolean;
  return_charged: boolean;
  
  // Rental Selection
  mainArea: string,
  subArea: string,
  orderType: string,
  hotel: string,
  deliveryAddr: string,
  mainCategory: string,
  delivery_charge: number,
  packageData: Array<any>, // Store API response with package details
  selectedDuration: string | null, // Currently selected duration ID
}

interface ContextProps {
  ReservationMain: ReservationMainProps;
  ReservationItems: Array<any>;
  isCalculating: boolean;
  setReservationMain: (ReservationMain: ReservationMainProps) => void;
  setReservationValue: (key: keyof ReservationMainProps, value: any) => void;
  getReservationValue: (key: keyof ReservationMainProps) => any | null;
  addReservationItems: (items: Array<any>) => void;
  removeReservationItem: (index: number) => void;
  initReservation: () => void;
}

const initializedMain: ReservationMainProps = {
  pickup: null,
  dropoff: null,
  prices: {
    subtotal: 0,
    tax: 0,
    discount: 0,
    service_fee: 0,
    total: 0,
  },
  price_table_id: null,
  name: "",
  note: "",
  address: "",
  address2: "",
  city: "",
  state: "",
  state_id: 0,
  postal_code: "",
  phone_number: "",
  email: "",
  password: "",
  is_accept: false,
  img_url: "",
  address_id: null,
  selectedAddress: null,
  use_manual: false,
  manual_address: "",
  usps_address: "",
  mapbox_id: null,
  lat: null,
  lng: null,
  driver_tip: 0.00,
  discount_code: "",
  promo_code: null,
  discount_rate: null,
  headerData: [],
  priceTableData: [],
  availableSheet: null,
  license_file: null,
  additional_license_files: null,
  insurance_file: null,
  additional_insurance_files: null,
  agreement_file: null,
  pickup_time: null,
  dropoff_time: null,
  discountApplied: false,
  bodhi_protect_opted_out: true,
  bodhi_protect_price: 0,
  
  // Driving Questions
  is_only_driver: null,
  additional_drivers: null,
  confirm_drivers: false,
  speed_limit_agreement: false,
  damage_responsibility: false,
  return_charged: false,

  // Rental Selection
  mainArea: "",
  subArea: "",
  orderType: "",
  hotel: "",
  deliveryAddr: "",
  mainCategory: "",
  delivery_charge: 0.00,
  packageData: [],
  selectedDuration: null,
}

export const CustomerReservationContext = createContext<ContextProps>({
  isCalculating: false,
  ReservationMain: initializedMain,
  ReservationItems: [],
  setReservationMain: () => {},
  setReservationValue: () => {},
  getReservationValue: () => null,
  addReservationItems: () => {},
  removeReservationItem: () => {},
  initReservation: () => {},
});

export const CustomerReservationProvider = ({ children }:{children:React.ReactNode}) => {
  const { storeDetails, discounts, priceLogicData } = useStoreDetails();

  const [ReservationMain, setReservationMain] = useState<ReservationMainProps>(initializedMain);
  const [ReservationItems, setReservationItems] = useState<Array<any>>([]);
  const [isCalculating, setIsCalculating] = useState<boolean>(false);

  const setReservationValue = (key: keyof ReservationMainProps, value: any) => {
    setReservationMain((prev) => {
      if (prev) {
        return { ...prev, [key]: value };
      } else {
        return { ...initializedMain, [key]: value }
      }
    });
  };
  
  const getReservationValue = (key: keyof ReservationMainProps): any | null => {
    return ReservationMain ? ReservationMain[key] : null;
  };

  const addReservationItems = (items:Array<any>) => {
    const updatedItems = [...ReservationItems, ...items];
    calcAndSetData(updatedItems);
  }

  const removeReservationItem = (index:number) => {
    const updatedItems =  [...ReservationItems];
    updatedItems.splice(index, 1);
    calcAndSetData(updatedItems);
  }

  const calcAndSetData = async (ReservationItems:Array<any>) =>{
    setIsCalculating(true);
    const calculatedReservedItems = await calculatePricedEquipmentData(ReservationMain.headerData, ReservationMain.price_table_id, ReservationMain.priceTableData, ReservationItems, ReservationMain.pickup, ReservationMain.dropoff);

    setReservationItems(calculatedReservedItems);

    let prices = {
      subtotal: 0,
      tax: 0,
      discount: 0,
      service_fee: 0,
      total: 0,
    }

    calculatedReservedItems.map((item:any)=>{
      let subtotal = item.price || 0;
      prices.subtotal += subtotal;
    });

    if(ReservationMain.discount_code){
      const selectedDiscount:any = discounts.find((item:any) => {
        if (typeof item.code === 'string') {
          return item.code.toLowerCase() === ReservationMain.discount_code.toLowerCase();
        }
        return false;
      });

      if(selectedDiscount){
        prices.discount = (Math.round(prices.subtotal * selectedDiscount.amount) / 100);
      }else{
        prices.discount = 0;
      }
    }
    
    // Calculate service fee if enabled
    if (storeDetails?.is_service_fee_enabled && storeDetails?.service_fee_percentage) {
      const serviceFeePercentage = parseFloat(storeDetails.service_fee_percentage) || 0;
      prices.service_fee = (prices.subtotal - prices.discount) * (serviceFeePercentage / 100);
    }
    
    prices.tax = (prices.subtotal - prices.discount + ReservationMain.driver_tip + ReservationMain.delivery_charge + prices.service_fee) * (storeDetails.sales_tax?storeDetails.sales_tax/100:0) || 0;
    prices.total = prices.subtotal - prices.discount + ReservationMain.driver_tip + ReservationMain.delivery_charge + prices.service_fee + prices.tax;

    // Include Bodhi Protect in subtotal only when not opted out
    if (!ReservationMain.bodhi_protect_opted_out) {
      prices.total += (ReservationMain.bodhi_protect_price || 0);
    }

    setReservationValue('prices', prices);
    setIsCalculating(false);
  }

  useEffect(() => {
    if(ReservationMain.pickup){
      const priceTable = getPriceTableByBrandAndDate(priceLogicData, storeDetails.brand_id, ReservationMain.pickup);
      setReservationValue('price_table_id', priceTable?.id??null);
    }
  }, [priceLogicData, storeDetails.brand_id, ReservationMain.pickup])

  useEffect(()=>{
    if(ReservationMain.pickup && ReservationMain.dropoff){
      const payload = {
        start_date: formatDateString(ReservationMain.pickup),
        end_date: formatDateString(ReservationMain.dropoff)
      }
      getAvaliableSheet(payload, (jsonRes:any, status, error) => {
        if(jsonRes) setReservationValue('availableSheet', jsonRes);
        else setReservationValue('availableSheet', null);
      })
    }else setReservationValue('availableSheet', null);
  }, [ReservationMain.pickup, ReservationMain.dropoff])

  useEffect(() => {
    if(ReservationMain.price_table_id){
      getHeaderData(ReservationMain.price_table_id, (jsonRes:any, status, error) => {
        switch (status) {
          case 200:
            setReservationValue('headerData', jsonRes);
            break;
          default:
            setReservationValue('headerData', []);
            break;
        }
      });
      getTableData(ReservationMain.price_table_id, (jsonRes:any, status, error) => {
        switch (status) {
          case 200:
            setReservationValue('priceTableData', jsonRes);
            break;
          default:
            setReservationValue('priceTableData', []);
            break;
        }
      });
    }else {
      setReservationValue('priceTableData', []);
    }
  }, [ReservationMain.price_table_id]);

  // Fetch delivery charge when address_id changes and orderType is delivery
  useEffect(() => {
    if (ReservationMain.address_id && ReservationMain.orderType === 'delivery' && storeDetails.id) {
      getDeliveryCharge({ address_id: ReservationMain.address_id, store_id: storeDetails.id }, (jsonRes: any, status: any) => {
        if (status === 200 && jsonRes) {
          // Assuming the API returns { delivery_charge: number } or just a number
          const charge = typeof jsonRes === 'number' ? jsonRes : (jsonRes.delivery_charge || jsonRes.charge || 0);
          setReservationValue('delivery_charge', parseFloat(charge));
        } else {
          setReservationValue('delivery_charge', 0.00);
        }
      });
    } else if (ReservationMain.orderType === 'ride_off' || !ReservationMain.address_id) {
      // Reset delivery charge for pickup orders or when no address is selected
      setReservationValue('delivery_charge', 0.00);
    }
  }, [ReservationMain.address_id, ReservationMain.orderType, storeDetails.id]);

  useEffect(()=>{
    calcAndSetData(ReservationItems);
  }, [
    ReservationMain.headerData, 
    ReservationMain.price_table_id, 
    ReservationMain.priceTableData, 
    ReservationMain.pickup, 
    ReservationMain.dropoff, 
    ReservationMain.driver_tip, 
    ReservationMain.discount_code, 
    discounts, 
    storeDetails.sales_tax,
    ReservationMain.bodhi_protect_opted_out,
    ReservationMain.bodhi_protect_price,
    ReservationMain.delivery_charge,
    storeDetails.is_service_fee_enabled,
    storeDetails.service_fee_percentage
  ]);
  
  const values: ContextProps = {
    isCalculating,
    ReservationMain,
    ReservationItems,
    setReservationMain,
    setReservationValue,
    getReservationValue,
    addReservationItems,
    removeReservationItem,
    initReservation: () => {
      setReservationMain(initializedMain);
      setReservationItems([]);
    },
  };

  return <CustomerReservationContext.Provider value={values}>{children}</CustomerReservationContext.Provider>;
};